import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle, Check, CheckCircle2, ChevronLeft, ChevronRight, Clock3, History,
  Keyboard, Pencil, Search, ShieldAlert, X, CalendarOff, ListChecks, MoreHorizontal,
} from 'lucide-react'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Modal, apiGet, adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import { Combobox, SegmentedControl, DatePicker } from '../admin/pickers.js'
import { Field, TextInput, NumberInput, Switch, Checkbox } from '../admin/form.js'
import { Floating } from '../shell/floating.js'
import { useConfirm } from '../shell/confirm.js'
import { humanName } from './format.js'
import { useMenuPersonne } from './menu-personne.js'
import './rh.css'
import './validation.css'

/**
 * SIRH LOT 4.6 — VALIDATION : POSTE DE TRAVAIL MASTER/DETAIL
 * (/equipe/temps/validation).
 *
 * À gauche, les salariés de la semaine avec leur ÉTAT (validé · à valider ·
 * anomalie · non-conformité) ; à droite, le salarié choisi : hero, journées
 * (prévu / pointé / validé, pause, écart, anomalies) et un INSPECTOR de
 * journée où tout se décide — validation, correction motivée, résolution
 * d'anomalie, dérogation. Aucune modale centrale pour travailler : la seule
 * modale est la confirmation SENSIBLE d'une validation exceptionnelle.
 *
 * Rien n'est calculé ici : durées rémunérées, verdicts, statuts et historique
 * viennent du cœur (`validationPeople`, `personValidationWeek`). L'écran
 * affiche, l'utilisateur décide, le serveur tranche.
 */

/* ─── types (miroir du cœur) ─── */
type PersonStatus = 'VALIDATED' | 'TO_APPROVE' | 'ANOMALY' | 'NON_CONFORM' | 'NOTHING'
interface PersonRow {
  person_id: string; display_name: string; position: string | null
  planned_minutes: number; clocked_minutes: number; approved_paid_minutes: number; worked_paid_minutes: number
  proposed_days: number; approved_days: number; corrected_days: number; days_total: number
  anomalies_open: number; non_conform_days: number; late_count: number; absence_count: number
  status: PersonStatus
}
interface PeopleResponse { week: string; people: PersonRow[]; totals: { persons: number; to_handle: number; validated: number; anomalies: number; proposed_days: number } }
interface Verdict { code: string; rule: string; severity: 'INFO' | 'WARNING' | 'BLOCK'; message: string; source: string; article: string | null }
interface Anomaly { id: string; kind: string; detail: string; resolved: boolean }
interface Version {
  id: string; status: string; starts_at: string; ends_at: string; break_minutes: number; break_paid: boolean; paid_minutes: number
  proposed_at: string; decided_at: string | null; decided_by_name: string | null
  correction_reason_code: string | null; correction_note: string | null
  legal_exception: boolean; legal_exception_reason: string | null; legal_exception_rule: string | null; superseded_by: string | null
}
interface Day {
  person_id: string; display_name: string; work_date: string
  planned: { starts_at: string; ends_at: string; break_minutes: number; break_paid: boolean; paid_minutes: number } | null
  clocked: { starts_at: string; ends_at: string } | null
  entry: {
    id: string; status: string; starts_at: string; ends_at: string; minutes: number; night_minutes: number
    break_minutes: number; break_paid: boolean; paid_minutes: number
    correction_reason_code: string | null; correction_note: string | null
    legal_exception: boolean; legal_exception_reason: string | null
  } | null
  delta_minutes: number | null
  anomalies: Anomaly[]
  verdicts: Verdict[]
  history: Version[]
  counter_preview: { movements: Array<{ type_key: string; amount: number; reason: string }>; note: string }
  /** Au moins un pointage sur la journée métier (exposé par le cœur de façon rétro-compatible — absent = inconnu). */
  has_clock?: boolean | undefined
}
interface DetailResponse {
  week: string
  person: { person_id: string; display_name: string; position: string | null }
  summary: PersonRow & { progress: { approved: number; total: number } }
  days: Day[]
}
/** Absence servie par /planning/context — APPROVED **ou** REQUESTED (4.7) ; statut absent = forme historique (approuvée). */
interface Leave { person_id: string; starts_on: string; ends_on: string; type_key: string; type_label: string; status?: string | undefined }

type Filter = 'all' | 'todo' | 'anomalies' | 'validated'
type Tab = 'temps' | 'absences' | 'historique'

/* ─── helpers ─── */
const PARIS = 'Europe/Paris'
const NEXT_PREF_KEY = 'gestion.validation.next'
function addDays(ymd: string, n: number): string { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10) }
function mondayOf(ymd: string): string { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7)); return d.toISOString().slice(0, 10) }
function todayYmd(): string { return new Intl.DateTimeFormat('fr-CA', { timeZone: PARIS, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()) }
function parisYmd(iso: string): string { return new Intl.DateTimeFormat('fr-CA', { timeZone: PARIS, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso)) }
function parisHm(iso: string): string { return new Intl.DateTimeFormat('fr-FR', { timeZone: PARIS, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)) }
/** Heure de Paris → instant réel, DST compris (essaie les deux décalages). */
function parisLocalToIso(ymd: string, hm: string): string {
  for (const off of ['+02:00', '+01:00']) { const d = new Date(`${ymd}T${hm}:00${off}`); if (!Number.isNaN(d.getTime()) && parisYmd(d.toISOString()) === ymd && parisHm(d.toISOString()) === hm) return d.toISOString() }
  return new Date(`${ymd}T${hm}:00+01:00`).toISOString()
}
function isoWeek(ymd: string): number {
  const d = new Date(`${ymd}T12:00:00Z`)
  const day = (d.getUTCDay() + 6) % 7
  d.setUTCDate(d.getUTCDate() - day + 3)
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4))
  return 1 + Math.round(((d.getTime() - firstThu.getTime()) / 86_400_000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7)
}
const fmtMin = (m: number): string => { const a = Math.abs(Math.round(m)); return `${m < 0 ? '−' : ''}${Math.floor(a / 60)} h ${String(a % 60).padStart(2, '0')}` }
const fmtDelta = (m: number): string => `${m > 0 ? '+' : m < 0 ? '−' : ''}${Math.abs(m)} min`
const frShort = (ymd: string): string => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' })
const frNum = (ymd: string): string => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' })
const frDayShort = (ymd: string): string => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
const frDayLong = (ymd: string): string => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
const frDateTime = (iso: string): string => new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: PARIS })
function weekLabel(week: string): string {
  const end = addDays(week, 6)
  const sameMonth = week.slice(0, 7) === end.slice(0, 7)
  const a = sameMonth ? String(Number(week.slice(8))) : frShort(week)
  return `Semaine ${isoWeek(week)} · ${a} – ${frShort(end)}`
}
const initials = (name: string): string => humanName(name).split(/[\s\-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('')
const range = (a: string, b: string): string => `${parisHm(a)} → ${parisHm(b)}`
const breakLabel = (min: number, paid: boolean): string => min > 0 ? `${min} min · ${paid ? 'Payée' : 'Non payée'}` : 'Aucune'

const ANOMALY_LABELS: Record<string, string> = {
  MISSING_IN: 'Entrée manquante', MISSING_OUT: 'Sortie manquante',
  DOUBLE_IN: 'Double entrée', DOUBLE_OUT: 'Double sortie',
  UNPLANNED_CLOCK: 'Hors planning', EARLY_CLOCK: 'Arrivée très en avance',
  LATE_CLOCK: 'Départ très tardif', DEVICE_CLOCK_DRIFT: 'Horloge appareil décalée',
  OFFLINE_LATE_SYNC: 'Synchronisation tardive (hors ligne)', OVERLAP: 'Conflit planning/absence', OTHER: 'Autre',
}
const REASONS = [
  { value: 'OUBLI_POINTAGE', label: 'Oubli de pointage' }, { value: 'ERREUR_POINTAGE', label: 'Erreur de pointage' },
  { value: 'TEMPS_NON_TRAVAILLE', label: 'Temps non travaillé' }, { value: 'DEPASSEMENT_VALIDE', label: 'Dépassement validé' },
  { value: 'AUTRE', label: 'Autre' },
]
const reasonLabel = (code: string | null): string => REASONS.find((r) => r.value === code)?.label ?? code ?? ''

const STATUS_META: Record<PersonStatus, { label: string; tone: string }> = {
  VALIDATED: { label: 'Validé', tone: 'ok' }, TO_APPROVE: { label: 'À valider', tone: 'todo' },
  ANOMALY: { label: 'Anomalie', tone: 'warn' }, NON_CONFORM: { label: 'Non conforme', tone: 'bad' }, NOTHING: { label: 'Rien à valider', tone: 'idle' },
}

/** État d'une journée, dérivé du modèle du cœur (aucune règle métier — une simple lecture). */
type DayState = 'APPROVED' | 'CORRECTED' | 'EXCEPTION' | 'PROPOSED' | 'NON_CONFORM' | 'ANOMALY' | 'CLOCKED' | 'ABSENCE' | 'PLANNED' | 'NONE'
const hasOpenAnomaly = (d: Day): boolean => d.anomalies.some((a) => !a.resolved)
/** « Prévu, non pointé » au sens du cœur : prévu, sans proposition, sans anomalie ouverte et sans aucun pointage sur la journée métier. */
const isUnclocked = (d: Day): boolean => !!d.planned && !d.clocked && !d.entry && !hasOpenAnomaly(d) && d.has_clock !== true
function dayState(d: Day, now: number): DayState {
  const open = hasOpenAnomaly(d)
  if (d.entry) {
    if (d.entry.status === 'PROPOSED') return d.verdicts.some((v) => v.severity === 'BLOCK') ? 'NON_CONFORM' : open ? 'ANOMALY' : 'PROPOSED'
    if (d.entry.legal_exception) return 'EXCEPTION'
    return d.entry.status === 'CORRECTED' ? 'CORRECTED' : 'APPROVED'
  }
  if (open) return 'ANOMALY'
  // Un pointage existe mais rien n'en découle (encore) : ni absence, ni anomalie — le cœur a tranché.
  if (d.clocked || d.has_clock === true) return 'CLOCKED'
  if (d.planned) return new Date(d.planned.ends_at).getTime() < now ? 'ABSENCE' : 'PLANNED'
  return 'NONE'
}
const DAY_META: Record<DayState, { label: string; tone: string }> = {
  APPROVED: { label: 'Validé', tone: 'ok' }, CORRECTED: { label: 'Corrigé', tone: 'ok' }, EXCEPTION: { label: 'Dérogation', tone: 'ok' },
  PROPOSED: { label: 'À valider', tone: 'todo' }, NON_CONFORM: { label: 'Non conforme', tone: 'bad' }, ANOMALY: { label: 'Anomalie', tone: 'warn' },
  CLOCKED: { label: 'Pointé', tone: 'idle' }, ABSENCE: { label: 'Absence', tone: 'warn' }, PLANNED: { label: 'Prévu', tone: 'idle' }, NONE: { label: '—', tone: 'idle' },
}
const isToHandle = (s: PersonStatus) => s === 'TO_APPROVE' || s === 'ANOMALY' || s === 'NON_CONFORM'
const isEditableTarget = (t: EventTarget | null): boolean => {
  const el = t as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

/* ═══════════════════════════ PAGE ═══════════════════════════ */

export function HrValidationWorkstation() {
  const qs = new URLSearchParams(window.location.search)
  const [week, setWeek] = useState(() => mondayOf(qs.get('week') ?? todayYmd()))
  const [person, setPerson] = useState<string | null>(qs.get('person'))
  const [filter, setFilter] = useState<Filter>(() => {
    const f = qs.get('filter'); if (f === 'todo' || f === 'anomalies' || f === 'validated') return f
    return qs.get('filtre') === 'anomalies' ? 'anomalies' : 'all' // deep-link historique de l'Action Center
  })
  const [q, setQ] = useState(qs.get('q') ?? '')
  const [day, setDay] = useState<string | null>(qs.get('day'))
  const [tab, setTab] = useState<Tab>('temps')
  const [chain, setChain] = useState<boolean>(() => { try { return localStorage.getItem(NEXT_PREF_KEY) !== '0' } catch { return true } })
  const [helpOpen, setHelpOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLButtonElement>(null)

  const [people, setPeople] = useState<PeopleResponse | null>(null)
  const [loadingPeople, setLoadingPeople] = useState(true)
  const [peopleFailed, setPeopleFailed] = useState<string | null>(null) // distinct du vide : un échec n'est jamais « rien à valider »
  const [detail, setDetail] = useState<DetailResponse | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  // Une réponse n'est appliquée que si elle est la DERNIÈRE demandée (semaine ou salarié changés entre-temps = périmée).
  const peopleSeq = useRef(0)
  const detailSeq = useRef(0)
  const [leaves, setLeaves] = useState<Leave[] | null>(null)
  const [exceptionFor, setExceptionFor] = useState<Day | null>(null)
  const [correcting, setCorrecting] = useState<{ work_date: string; start: string; end: string } | null>(null)

  const toast = useToast()
  const confirm = useConfirm()
  const perms = usePermissions()
  const can = (k: string) => !perms.loading && perms.permissions.includes(k)
  const canApprove = can('time.approve'), canCorrect = can('time.correct'), canException = can('time.approve.exception')
  const menuPersonne = useMenuPersonne()
  const autoPicked = useRef<string | null>(null)

  /* ─── chargement ─── */
  const loadPeople = useCallback(async () => {
    const seq = ++peopleSeq.current
    setLoadingPeople(true); setPeopleFailed(null)
    try {
      const r = await apiGet<PeopleResponse & { error?: string; message?: string }>(`/api/v1/hr/time/validation/people?week=${week}`)
      if (seq !== peopleSeq.current) return
      if ('people' in r && Array.isArray(r.people)) setPeople(r)
      else { setPeople(null); setPeopleFailed(r.message ?? 'Le serveur a refusé la liste des salariés.') }
    } catch {
      if (seq !== peopleSeq.current) return
      setPeople(null); setPeopleFailed('Le serveur ne répond pas.'); toast('error', 'Chargement des salariés impossible.')
    } finally { if (seq === peopleSeq.current) setLoadingPeople(false) }
  }, [week, toast])

  const loadDetail = useCallback(async (pid: string) => {
    const seq = ++detailSeq.current
    setLoadingDetail(true)
    try {
      const r = await apiGet<DetailResponse & { error?: string; message?: string }>(`/api/v1/hr/time/validation/person/${pid}?week=${week}`)
      if (seq !== detailSeq.current) return
      if ('days' in r && Array.isArray(r.days)) setDetail(r)
      else { setDetail(null); toast('error', r.message ?? 'Chargement du salarié impossible.') }
    } catch {
      if (seq !== detailSeq.current) return
      setDetail(null); toast('error', 'Chargement du salarié impossible.')
    } finally { if (seq === detailSeq.current) setLoadingDetail(false) }
  }, [week, toast])

  useEffect(() => { void loadPeople() }, [loadPeople])
  // Changer de salarié efface le détail de l'autre AVANT la réponse : le hero ne décrit jamais A pendant que les actions ciblent B.
  useEffect(() => {
    if (person) { setDetail((cur) => cur && cur.person.person_id === person ? cur : null); void loadDetail(person) }
    else { detailSeq.current++; setDetail(null); setLoadingDetail(false) }
  }, [person, loadDetail])
  // Absences (approuvées ET demandes en attente) : lecture opportuniste (permission planning) — absente, l'onglet le dit.
  useEffect(() => {
    apiGet<{ leaves?: Leave[] }>(`/api/v1/hr/planning/context?week=${week}`)
      .then((r) => setLeaves('leaves' in r && Array.isArray(r.leaves) ? r.leaves : null)).catch(() => setLeaves(null))
  }, [week])

  const reload = useCallback(async () => { if (person) await loadDetail(person); await loadPeople() }, [person, loadDetail, loadPeople])

  /* ─── URL = état complet (SmartBack retrouve tout) ─── */
  useEffect(() => {
    const p = new URLSearchParams()
    p.set('week', week)
    if (person) p.set('person', person)
    if (filter !== 'all') p.set('filter', filter)
    if (q) p.set('q', q)
    if (day) p.set('day', day)
    window.history.replaceState(window.history.state, '', `/equipe/temps/validation?${p.toString()}`)
  }, [week, person, filter, q, day])
  useEffect(() => { try { localStorage.setItem(NEXT_PREF_KEY, chain ? '1' : '0') } catch { /* stockage indisponible */ } }, [chain])

  /* ─── liste filtrée ─── */
  const visible = useMemo(() => {
    const list = people?.people ?? []
    const s = q.trim().toLowerCase()
    return list.filter((p) => {
      if (filter === 'todo' && !isToHandle(p.status)) return false
      if (filter === 'anomalies' && !(p.status === 'ANOMALY' || p.status === 'NON_CONFORM')) return false
      if (filter === 'validated' && p.status !== 'VALIDATED') return false
      return !s || p.display_name.toLowerCase().includes(s) || (p.position ?? '').toLowerCase().includes(s)
    })
  }, [people, filter, q])

  // Sélection automatique du premier salarié À TRAITER sur grand écran — zéro clic pour commencer.
  useEffect(() => {
    if (person || !people || autoPicked.current === week) return
    if (!window.matchMedia('(min-width: 900px)').matches) return
    const first = visible.find((p) => isToHandle(p.status)) ?? visible[0]
    if (first) { autoPicked.current = week; setPerson(first.person_id) }
  }, [people, visible, person, week])

  const selectPerson = useCallback((pid: string | null) => { setPerson(pid); setDay(null); setCorrecting(null); setTab('temps') }, [])
  const stepPerson = useCallback((dir: 1 | -1) => {
    if (visible.length === 0) return
    const idx = visible.findIndex((p) => p.person_id === person)
    const next = visible[(idx + dir + visible.length) % visible.length]
    if (next) selectPerson(next.person_id)
  }, [visible, person, selectPerson])

  const changeWeek = (ymd: string) => { setWeek(mondayOf(ymd)); setDay(null); setCorrecting(null); autoPicked.current = null }

  /* ─── actions ─── */
  const now = Date.now()
  const days = detail?.days ?? []
  const selectedDay = days.find((d) => d.work_date === day) ?? null

  const approveDay = useCallback(async (d: Day) => {
    if (!d.entry) return
    const r = await adminMutate<{ error?: string; message?: string; requires_exception?: boolean; warnings?: Array<{ message: string }> }>('POST', `/api/v1/hr/time/entries/${d.entry.id}/approve`, {})
    if (r.status >= 400) {
      if (r.data.requires_exception) { setExceptionFor(d); return }
      toast('error', r.data.message ?? 'Validation refusée.'); return
    }
    toast('ok', `${frDayLong(d.work_date)} validée${r.data.warnings?.length ? ` — à noter : ${r.data.warnings[0]!.message}` : ''}.`)
    await reload()
  }, [toast, reload])

  const goNext = useCallback(async (after: string) => {
    const r = await apiGet<{ next?: string | null }>(`/api/v1/hr/time/validation/next?week=${week}&after=${after}`)
    if ('next' in r && r.next) { selectPerson(r.next); toast('ok', 'Salarié suivant à traiter.') }
    else toast('ok', 'Tous les salariés de la semaine sont traités.')
  }, [week, selectPerson, toast])

  const approveWeek = useCallback(async () => {
    if (!person) return
    const r = await adminMutate<{ error?: string; message?: string; approved?: number; skipped?: Array<{ work_date: string; reason: string; message: string }> }>('POST', '/api/v1/hr/time/entries/approve-week', { week, person_id: person })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Validation refusée.'); return }
    const n = r.data.approved ?? 0; const sk = r.data.skipped ?? []
    toast(n > 0 ? 'ok' : 'error', `${n} journée${n > 1 ? 's' : ''} validée${n > 1 ? 's' : ''}${sk.length ? ` · ${sk.length} laissée${sk.length > 1 ? 's' : ''} à traiter (${sk.map((s) => s.reason === 'NON_CONFORM' ? 'dérogation' : 'anomalie').join(', ')})` : ''}.`)
    await reload()
    if (sk.length === 0 && chain) await goNext(person)
  }, [person, week, toast, reload, chain, goNext])

  const bulkClean = useCallback(async () => {
    const dry = await adminMutate<{ error?: string; message?: string; count?: number }>('POST', '/api/v1/hr/time/entries/approve-clean', { week, dry_run: true })
    if (dry.status >= 400) { toast('error', dry.data.message ?? 'Aperçu impossible.'); return }
    const count = dry.data.count ?? 0
    if (count === 0) { toast('ok', 'Aucune journée conforme et sans anomalie à valider en masse.'); return }
    const ok = await confirm({
      title: 'Valider toutes les journées conformes de la semaine ?',
      message: `${count} journée${count > 1 ? 's' : ''} sans anomalie ${count > 1 ? 'seront validées' : 'sera validée'} pour tous les salariés de votre périmètre. Les journées avec anomalie non résolue ou non-conformité ne sont jamais validées en masse.`,
      variant: 'sensitive', confirmLabel: `Valider ${count} journée${count > 1 ? 's' : ''}`,
    })
    if (!ok) return
    const r = await adminMutate<{ error?: string; message?: string; approved?: number; skipped?: unknown[] }>('POST', '/api/v1/hr/time/entries/approve-clean', { week })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Échec.'); return }
    toast('ok', `${r.data.approved ?? 0} journée${(r.data.approved ?? 0) > 1 ? 's' : ''} validée${(r.data.approved ?? 0) > 1 ? 's' : ''}${r.data.skipped?.length ? ` · ${r.data.skipped.length} nécessite${r.data.skipped.length > 1 ? 'nt' : ''} une dérogation` : ''}.`)
    await reload()
  }, [week, toast, confirm, reload])

  /* ─── clavier (jamais obligatoire, désactivé dans les champs, sous une modale et sous le menu clic droit) ─── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // `.lc-ctx` : le menu clic droit ouvert prend les flèches pour lui ; sans ça, ↓ changeait aussi de salarié.
      if (isEditableTarget(e.target) || document.querySelector('.lc-modal-overlay, .lc-confirm-overlay, .lc-ctx')) return
      if (e.key === '?' ) { setHelpOpen(true); e.preventDefault(); return }
      if (e.key === 'j' || e.key === 'ArrowDown') { stepPerson(1); e.preventDefault(); return }
      if (e.key === 'k' || e.key === 'ArrowUp') { stepPerson(-1); e.preventDefault(); return }
      if (e.key === 'Escape') { if (correcting) setCorrecting(null); else if (day) setDay(null); return }
      // Ctrl/Cmd+Entrée : la journée AFFICHÉE, et rien d'autre — mêmes refus que le bouton « Valider ».
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        if (!selectedDay) { toast('error', 'Aucune journée ouverte — Entrée ouvre la première journée à valider, puis Ctrl+Entrée la valide.'); return }
        if (!canApprove) { toast('error', 'Vous n’avez pas la permission de valider.'); return }
        if (selectedDay.entry?.status !== 'PROPOSED') { toast('error', selectedDay.entry ? `${frDayLong(selectedDay.work_date)} est déjà validée.` : `Aucune proposition à valider pour ${frDayLong(selectedDay.work_date)}.`); return }
        if (selectedDay.verdicts.some((v) => v.severity === 'BLOCK')) { toast('error', 'Journée non conforme : validable uniquement en dérogation motivée, depuis l’inspecteur.'); return }
        if (hasOpenAnomaly(selectedDay)) { toast('error', 'Résolvez les anomalies de la journée avant de la valider.'); return }
        void approveDay(selectedDay)
        return
      }
      // Entrée seule : uniquement hors d'un élément qui a déjà son propre sens (bouton, lien, ligne focalisée).
      const tag = (e.target as HTMLElement | null)?.tagName
      if (e.key === 'Enter' && !day && days.length && tag !== 'BUTTON' && tag !== 'A' && tag !== 'TR') {
        const target = days.find((d) => d.entry?.status === 'PROPOSED') ?? days[0]!
        setDay(target.work_date); e.preventDefault()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [stepPerson, day, days, selectedDay, correcting, canApprove, approveDay, toast])

  /* ─── dérivés du détail ─── */
  const summary = detail?.summary ?? null
  const proposed = days.filter((d) => d.entry?.status === 'PROPOSED')
  const blocked = proposed.filter((d) => d.verdicts.some((v) => v.severity === 'BLOCK'))
  const withAnomaly = proposed.filter((d) => !d.verdicts.some((v) => v.severity === 'BLOCK') && hasOpenAnomaly(d))
  const clean = proposed.length - blocked.length - withAnomaly.length
  // Anomalies ouvertes de TOUTE la semaine (avec ou sans proposition) et absences : ce qui empêche de dire « validée ».
  const anomalyDays = days.filter(hasOpenAnomaly)
  const openAnomalies = anomalyDays.reduce((n, d) => n + d.anomalies.filter((a) => !a.resolved).length, 0)
  const absenceDays = days.filter((d) => dayState(d, now) === 'ABSENCE')
  const weekValidated = summary !== null && (summary.status === 'VALIDATED'
    || (openAnomalies === 0 && summary.anomalies_open === 0 && absenceDays.length === 0 && summary.absence_count === 0 && summary.progress.total > 0))
  const openDay = (d: Day) => { setTab('temps'); setCorrecting(null); setDay(d.work_date) }
  const mode: 'list' | 'detail' = person ? 'detail' : 'list'

  return (
    <main className="lc-page lc-page--wide lc-stagger vws-page">
      <Trail />
      <SmartBack fallback="/equipe/temps" />

      <header className="vws-head">
        <div className="vws-head__title">
          <h1 className="lc-title"><ListChecks size={20} aria-hidden="true" /> Validation</h1>
          <p className="lc-subtitle">Prévu · pointé · validé — les trois vérités restent, la Direction tranche, salarié par salarié.</p>
        </div>
        <div className="vws-head__tools">
          <div className="vws-weeknav">
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Semaine précédente" onClick={() => changeWeek(addDays(week, -7))}><ChevronLeft size={16} /></button>
            <DatePicker value={week} onPick={changeWeek} label={weekLabel(week)} highlightFrom={week} highlightTo={addDays(week, 6)} ariaLabel="Choisir la semaine" />
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Semaine suivante" onClick={() => changeWeek(addDays(week, 7))}><ChevronRight size={16} /></button>
            {week !== mondayOf(todayYmd()) ? <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => changeWeek(todayYmd())}>Cette semaine</button> : null}
          </div>
          <label className="vws-chain" title="Après la validation complète d’un salarié, passer au prochain à traiter.">
            <Switch checked={chain} onChange={setChain} ariaLabel="Enchaîner les salariés" />
            <span>Enchaîner</span>
          </label>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Raccourcis clavier" onClick={() => setHelpOpen(true)}><Keyboard size={16} /></button>
          {canApprove ? (
            <>
              <button ref={menuRef} type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Actions de la semaine" aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}><MoreHorizontal size={16} /></button>
              <Floating open={menuOpen} anchorRef={menuRef} placement="bottom-end" onOutside={() => setMenuOpen(false)} className="lc-menu">
                <ul className="lc-menu__list" role="menu">
                  <li role="none"><button type="button" role="menuitem" className="lc-menu__item" onClick={() => { setMenuOpen(false); void bulkClean() }}><CheckCircle2 size={14} /><span>Valider toutes les journées conformes de la semaine</span></button></li>
                </ul>
              </Floating>
            </>
          ) : null}
        </div>
      </header>

      <div className="vws-body" data-mode={mode}>
        {/* ═══════ MASTER ═══════ */}
        <aside className="vws-master" aria-label="Salariés de la semaine">
          <div className="vws-master__tools">
            <label className="vws-search">
              <Search size={15} aria-hidden="true" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un salarié" aria-label="Rechercher un salarié" />
              {q ? <button type="button" className="vws-search__clear" aria-label="Effacer" onClick={() => setQ('')}><X size={13} /></button> : null}
            </label>
            <SegmentedControl<Filter> value={filter} onChange={setFilter} ariaLabel="Filtrer par état" size="sm"
              options={[
                { value: 'all', label: 'Tous' },
                { value: 'todo', label: people ? `À valider · ${people.totals.to_handle}` : 'À valider' },
                { value: 'anomalies', label: 'Anomalies' },
                { value: 'validated', label: 'Validés' },
              ]} />
          </div>
          {peopleFailed ? (
            <div className="vws-failed" role="alert">
              <p className="vws-failed__title"><AlertTriangle size={16} aria-hidden="true" /> Chargement impossible</p>
              <p className="vws-failed__text">{peopleFailed} Rien n’est perdu : la liste se recharge à la demande.</p>
              <button type="button" className="lc-btn" onClick={() => void loadPeople()}>Réessayer</button>
            </div>
          ) : loadingPeople && !people ? (
            <div className="vws-skel" aria-busy="true">{Array.from({ length: 8 }, (_, i) => <div key={i} className="vws-skel__row" />)}</div>
          ) : visible.length === 0 ? (
            <p className="vws-empty">
              {(people?.people.length ?? 0) === 0
                ? 'Aucune journée à valider cette semaine — les propositions naissent des pointages.'
                : 'Aucun salarié ne correspond à ce filtre.'}
            </p>
          ) : (
            <ul className="vws-people" role="listbox" aria-label="Salariés">
              {visible.map((p) => {
                const meta = STATUS_META[p.status]
                const sel = p.person_id === person
                return (
                  <li key={p.person_id} role="option" aria-selected={sel}>
                    <button type="button" className={`vws-person${sel ? ' vws-person--on' : ''}`} onClick={() => selectPerson(p.person_id)}>
                      <span className={`vws-avatar vws-avatar--${meta.tone}`} aria-hidden="true">{initials(p.display_name)}</span>
                      <span className="vws-person__main">
                        <span className="vws-person__name">{humanName(p.display_name)}</span>
                        <span className="vws-person__meta">
                          {p.position ? <span>{p.position}</span> : null}
                          <span className={`vws-status vws-status--${meta.tone}`}><span className="vws-status__dot" />{meta.label}</span>
                          {p.anomalies_open > 0 ? <span className="vws-person__anom">● {p.anomalies_open} anomalie{p.anomalies_open > 1 ? 's' : ''}</span> : null}
                        </span>
                      </span>
                      <span className="vws-person__hours">
                        <strong>{fmtMin(p.worked_paid_minutes)}</strong>
                        <small>{p.proposed_days > 0 ? `${p.proposed_days} à valider` : p.days_total > 0 ? `${p.approved_days + p.corrected_days} validée${p.approved_days + p.corrected_days > 1 ? 's' : ''}` : '—'}</small>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
          {people ? (
            <footer className="vws-master__foot">
              <span><strong>{people.totals.persons}</strong> salarié{people.totals.persons > 1 ? 's' : ''}</span>
              <span><strong>{people.totals.to_handle}</strong> à traiter</span>
              <span><strong>{people.totals.validated}</strong> validé{people.totals.validated > 1 ? 's' : ''}</span>
            </footer>
          ) : null}
        </aside>

        {/* ═══════ DETAIL ═══════ */}
        <section className="vws-detail" aria-live="polite">
          {!person ? (
            <div className="vws-placeholder">
              <Clock3 size={28} aria-hidden="true" />
              <p>{peopleFailed
                ? 'La liste des salariés n’a pas pu être chargée — réessayez depuis la colonne de gauche.'
                : people !== null && people.people.length === 0
                  ? 'Aucune journée à valider cette semaine — les propositions naissent des pointages.'
                  : 'Choisissez un salarié dans la liste pour valider sa semaine.'}</p>
            </div>
          ) : loadingDetail && !detail ? (
            <div className="vws-skel vws-skel--detail" aria-busy="true"><div className="vws-skel__hero" />{Array.from({ length: 5 }, (_, i) => <div key={i} className="vws-skel__row" />)}</div>
          ) : !detail || !summary ? (
            <div className="vws-placeholder"><p>Ce salarié n’est pas lisible ici (hors périmètre ou inconnu).</p></div>
          ) : (
            <>
              <button type="button" className="lc-crumb vws-backlist" onClick={() => selectPerson(null)}><ChevronLeft size={14} aria-hidden="true" /> Salariés</button>
              <header className="vws-hero">
                <div className="vws-hero__id" {...menuPersonne.personne({ id: detail.person.person_id, nom: detail.person.display_name })}>
                  <span className={`vws-avatar vws-avatar--lg vws-avatar--${STATUS_META[summary.status].tone}`} aria-hidden="true">{initials(detail.person.display_name)}</span>
                  <div>
                    <h2 className="vws-hero__name">{humanName(detail.person.display_name)}</h2>
                    <p className="vws-hero__sub">{detail.person.position ? `${detail.person.position} · ` : ''}{weekLabel(week)}</p>
                  </div>
                </div>
                <dl className="vws-kpis">
                  <div><dt>Rémunéré</dt><dd>{fmtMin(summary.worked_paid_minutes)}</dd><small>{summary.planned_minutes ? `prévu ${fmtMin(summary.planned_minutes)}` : 'rien de prévu'}</small></div>
                  <div><dt>Retards</dt><dd className={summary.late_count ? 'vws-kpi--warn' : ''}>{summary.late_count}</dd><small>&gt; 5 min après le prévu</small></div>
                  <div><dt>Absences</dt><dd className={summary.absence_count ? 'vws-kpi--warn' : ''}>{summary.absence_count}</dd><small>prévues, non pointées</small></div>
                  <div className="vws-kpi--progress">
                    <dt>Journées validées</dt>
                    <dd>{summary.progress.approved} / {summary.progress.total}</dd>
                    <span className="vws-progress" aria-hidden="true"><span style={{ width: `${summary.progress.total ? Math.round(summary.progress.approved / summary.progress.total * 100) : 0}%` }} /></span>
                  </div>
                </dl>
                <div className="vws-hero__actions">
                  {proposed.length === 0 ? (
                    weekValidated
                      ? <span className="vws-done"><CheckCircle2 size={16} aria-hidden="true" /> Semaine validée</span>
                      : anomalyDays[0]
                        ? <button type="button" className="vws-hero__link vws-hero__link--warn" onClick={() => openDay(anomalyDays[0]!)}><AlertTriangle size={15} aria-hidden="true" /> {openAnomalies} anomalie{openAnomalies > 1 ? 's' : ''} à résoudre · {frDayShort(anomalyDays[0].work_date)}</button>
                        : absenceDays[0]
                          ? <button type="button" className="vws-hero__link vws-hero__link--warn" onClick={() => openDay(absenceDays[0]!)}><CalendarOff size={15} aria-hidden="true" /> {absenceDays.length} journée{absenceDays.length > 1 ? 's' : ''} prévue{absenceDays.length > 1 ? 's' : ''}, non pointée{absenceDays.length > 1 ? 's' : ''} · {frDayShort(absenceDays[0].work_date)}</button>
                          : summary.progress.total > 0
                            ? <span className="vws-done vws-done--idle">{summary.progress.approved} / {summary.progress.total} journée{summary.progress.total > 1 ? 's' : ''} validée{summary.progress.total > 1 ? 's' : ''}</span>
                            : <span className="vws-done vws-done--idle">Aucune journée pointée</span>
                  ) : canApprove ? (
                    <>
                      <button type="button" className="lc-btn lc-btn--primary" disabled={clean === 0} onClick={() => void approveWeek()}>
                        <Check size={15} aria-hidden="true" />
                        {blocked.length === 0 && withAnomaly.length === 0 ? `Valider la semaine · ${proposed.length} journée${proposed.length > 1 ? 's' : ''}` : `Valider ${clean} journée${clean > 1 ? 's' : ''} conforme${clean > 1 ? 's' : ''}`}
                      </button>
                      {blocked.length || openAnomalies ? (
                        <span className="vws-hero__note">
                          {blocked.length ? `${blocked.length} journée${blocked.length > 1 ? 's' : ''} nécessite${blocked.length > 1 ? 'nt' : ''} une dérogation` : ''}
                          {blocked.length && openAnomalies ? ' · ' : ''}
                          {openAnomalies && anomalyDays[0] ? <button type="button" className="vws-hero__link" onClick={() => openDay(anomalyDays[0]!)}>{openAnomalies} anomalie{openAnomalies > 1 ? 's' : ''} à résoudre</button> : null}
                        </span>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </header>

              <SegmentedControl<Tab> value={tab} onChange={setTab} ariaLabel="Section" size="sm"
                options={[{ value: 'temps', label: 'Temps', icon: <Clock3 size={14} /> }, { value: 'absences', label: 'Absences planifiées', icon: <CalendarOff size={14} /> }, { value: 'historique', label: 'Historique & corrections', icon: <History size={14} /> }]} />

              {tab === 'temps' ? (
                <div className={`vws-split${selectedDay ? ' vws-split--inspect' : ''}`}>
                  <DaysTable days={days} now={now} selected={day} onSelect={(wd) => { setDay(wd === day ? null : wd); setCorrecting(null) }}
                    canApprove={canApprove} canCorrect={canCorrect} canException={canException}
                    onApprove={(d) => void approveDay(d)} onException={setExceptionFor}
                    onCorrect={(d, start, end) => { setDay(d.work_date); setCorrecting({ work_date: d.work_date, start, end }) }} />
                  {selectedDay ? (
                    <DayInspector key={`${selectedDay.work_date}:${selectedDay.entry?.id ?? 'none'}`} day={selectedDay} now={now}
                      canApprove={canApprove} canCorrect={canCorrect} canException={canException}
                      correcting={correcting?.work_date === selectedDay.work_date ? correcting : null}
                      onStartCorrection={() => selectedDay.entry && setCorrecting({ work_date: selectedDay.work_date, start: parisHm(selectedDay.entry.starts_at), end: parisHm(selectedDay.entry.ends_at) })}
                      onCancelCorrection={() => setCorrecting(null)}
                      onClose={() => { setDay(null); setCorrecting(null) }}
                      onApprove={() => void approveDay(selectedDay)} onException={() => setExceptionFor(selectedDay)}
                      onChanged={async () => { setCorrecting(null); await reload() }} />
                  ) : null}
                </div>
              ) : tab === 'absences' ? (
                <AbsencesTab days={days} now={now} leaves={leaves} personId={person} week={week} />
              ) : (
                <HistoryTab days={days} />
              )}
            </>
          )}
        </section>
      </div>

      {exceptionFor?.entry ? (
        <ExceptionModal day={exceptionFor} onClose={() => setExceptionFor(null)} onDone={async () => { setExceptionFor(null); await reload() }} />
      ) : null}

      <Modal open={helpOpen} onClose={() => setHelpOpen(false)} ariaLabel="Raccourcis clavier" width={460}>
        <div className="vws-help">
          <h3>Raccourcis clavier</h3>
          <p className="lc-hint">Jamais obligatoires — ils sont inactifs dans les champs de saisie.</p>
          <dl>
            <div><dt><kbd>J</kbd> / <kbd>↓</kbd></dt><dd>Salarié suivant</dd></div>
            <div><dt><kbd>K</kbd> / <kbd>↑</kbd></dt><dd>Salarié précédent</dd></div>
            <div><dt><kbd>Entrée</kbd></dt><dd>Ouvrir la première journée à valider</dd></div>
            <div><dt><kbd>Ctrl</kbd> + <kbd>Entrée</kbd></dt><dd>Valider la journée ouverte dans l’inspecteur — refusé tant qu’une anomalie reste ouverte ou qu’une règle bloque</dd></div>
            <div><dt><kbd>Échap</kbd></dt><dd>Fermer la correction, puis l’inspecteur</dd></div>
            <div><dt><kbd>?</kbd></dt><dd>Cette aide</dd></div>
          </dl>
          <div className="lc-modal__actions"><button type="button" className="lc-btn lc-btn--primary" onClick={() => setHelpOpen(false)}>Fermer</button></div>
        </div>
      </Modal>
    </main>
  )
}

/* ═══════════════════════════ TABLEAU DES JOURNÉES ═══════════════════════════ */

function DaysTable(props: {
  days: Day[]; now: number; selected: string | null
  onSelect: (workDate: string) => void
  canApprove: boolean; canCorrect: boolean; canException: boolean
  onApprove: (d: Day) => void; onException: (d: Day) => void
  onCorrect: (d: Day, start: string, end: string) => void
}) {
  const [inline, setInline] = useState<{ work_date: string; start: string; end: string } | null>(null)
  if (props.days.length === 0) {
    return <p className="vws-empty vws-empty--card">Aucune journée à valider cette semaine — les propositions naissent des pointages.</p>
  }
  return (
    <div className="vws-tablewrap">
      <table className="vws-table">
        <thead>
          <tr>
            <th scope="col">État</th><th scope="col">Jour</th><th scope="col">Prévu</th><th scope="col">Pause prévue</th>
            <th scope="col">Pointé</th><th scope="col">Validé · rémunéré</th><th scope="col">Pause validée</th>
            <th scope="col" className="vws-num">Écart</th><th scope="col">Anomalies</th><th scope="col" className="vws-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {props.days.map((d) => {
            const st = dayState(d, props.now); const meta = DAY_META[st]
            const block = d.verdicts.find((v) => v.severity === 'BLOCK')
            const open = d.anomalies.filter((a) => !a.resolved)
            const editing = inline?.work_date === d.work_date
            const sel = props.selected === d.work_date
            return (
              <tr key={d.work_date} className={`vws-row vws-row--${meta.tone}${sel ? ' vws-row--on' : ''}`} tabIndex={0} aria-selected={sel}
                onClick={() => props.onSelect(d.work_date)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !editing && e.target === e.currentTarget) { props.onSelect(d.work_date); e.preventDefault(); e.stopPropagation() } }}>
                <td><span className={`vws-status vws-status--${meta.tone}`}><span className="vws-status__dot" />{meta.label}</span></td>
                <td className="vws-day">{frDayShort(d.work_date)}</td>
                <td>{d.planned ? <><span className="vws-range">{range(d.planned.starts_at, d.planned.ends_at)}</span><small>{fmtMin(d.planned.paid_minutes)}</small></> : <span className="vws-muted">—</span>}</td>
                <td>{d.planned ? breakLabel(d.planned.break_minutes, d.planned.break_paid) : <span className="vws-muted">—</span>}</td>
                <td>{d.clocked ? <span className="vws-range">{range(d.clocked.starts_at, d.clocked.ends_at)}</span> : <span className="vws-muted">—</span>}</td>
                <td className="vws-approvedcell" onClick={(e) => e.stopPropagation()}>
                  {d.entry ? editing ? (
                    <span className="vws-inline" onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); props.onCorrect(d, inline!.start, inline!.end); setInline(null) }
                      if (e.key === 'Escape') { e.preventDefault(); setInline(null) }
                    }}>
                      <input type="time" className="lc-input vws-time" value={inline!.start} aria-label="Début validé" autoFocus onChange={(e) => setInline({ ...inline!, start: e.target.value })} />
                      <span aria-hidden="true">→</span>
                      <input type="time" className="lc-input vws-time" value={inline!.end} aria-label="Fin validée" onChange={(e) => setInline({ ...inline!, end: e.target.value })} />
                      <button type="button" className="lc-btn lc-btn--sm lc-btn--primary" onClick={() => { props.onCorrect(d, inline!.start, inline!.end); setInline(null) }}>Motif…</button>
                      <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon lc-btn--sm" aria-label="Annuler" onClick={() => setInline(null)}><X size={13} /></button>
                    </span>
                  ) : (
                    <button type="button" className="vws-editable" disabled={!props.canCorrect} title={props.canCorrect ? 'Cliquer pour corriger les horaires' : undefined}
                      onClick={() => setInline({ work_date: d.work_date, start: parisHm(d.entry!.starts_at), end: parisHm(d.entry!.ends_at) })}>
                      <span className="vws-range">{range(d.entry.starts_at, d.entry.ends_at)}</span>
                      <strong>{fmtMin(d.entry.paid_minutes)}</strong>
                      {props.canCorrect ? <Pencil size={12} aria-hidden="true" className="vws-editable__pen" /> : null}
                    </button>
                  ) : <span className="vws-muted">{st === 'ABSENCE' ? 'Non pointé' : '—'}</span>}
                </td>
                <td>{d.entry ? breakLabel(d.entry.break_minutes, d.entry.break_paid) : <span className="vws-muted">—</span>}</td>
                <td className={`vws-num${d.delta_minutes ? (d.delta_minutes > 0 ? ' vws-delta--plus' : ' vws-delta--minus') : ''}`}>{d.delta_minutes != null ? fmtDelta(d.delta_minutes) : <span className="vws-muted">—</span>}</td>
                <td>
                  {d.anomalies.length === 0 ? <span className="vws-muted">—</span> : open.length === 0
                    ? <span className="vws-anom vws-anom--done"><Check size={12} aria-hidden="true" /> {d.anomalies.length} résolue{d.anomalies.length > 1 ? 's' : ''}</span>
                    : open.slice(0, 2).map((a) => <span key={a.id} className="vws-anom" title={a.detail}><AlertTriangle size={12} aria-hidden="true" /> {ANOMALY_LABELS[a.kind] ?? a.kind}</span>)}
                  {open.length > 2 ? <span className="vws-anom">+{open.length - 2}</span> : null}
                </td>
                <td className="vws-actions" onClick={(e) => e.stopPropagation()}>
                  {d.entry?.status === 'PROPOSED' ? (
                    block
                      ? (props.canException ? <button type="button" className="lc-btn lc-btn--sm lc-btn--danger" onClick={() => props.onException(d)}><ShieldAlert size={13} aria-hidden="true" /> Dérogation</button> : <span className="vws-muted">Dérogation requise</span>)
                      : (props.canApprove ? <button type="button" className="lc-btn lc-btn--sm lc-btn--primary" disabled={open.length > 0} title={open.length ? 'Résoudre les anomalies d’abord' : undefined} onClick={() => props.onApprove(d)}><Check size={13} aria-hidden="true" /> Valider</button> : null)
                  ) : null}
                  <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => props.onSelect(d.work_date)}>{sel ? 'Fermer' : 'Détail'}</button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* ═══════════════════════════ INSPECTOR DE JOURNÉE ═══════════════════════════ */

function DayInspector(props: {
  day: Day; now: number
  canApprove: boolean; canCorrect: boolean; canException: boolean
  correcting: { work_date: string; start: string; end: string } | null
  onStartCorrection: () => void; onCancelCorrection: () => void
  onClose: () => void; onApprove: () => void; onException: () => void
  onChanged: () => Promise<void>
}) {
  const d = props.day
  const toast = useToast()
  const st = dayState(d, props.now); const meta = DAY_META[st]
  const block = d.verdicts.find((v) => v.severity === 'BLOCK')
  const open = d.anomalies.filter((a) => !a.resolved)
  const [resolution, setResolution] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const resolve = async (id: string) => {
    const text = (resolution[id] ?? '').trim()
    if (text.length < 3) return
    setBusy(true)
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/hr/time/anomalies/${id}/resolve`, { resolution: text })
    setBusy(false)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Résolution refusée.'); return }
    toast('ok', 'Anomalie résolue — tracée dans l’audit.')
    await props.onChanged()
  }
  const current = d.history.find((v) => v.superseded_by === null) ?? null

  return (
    <aside className="vws-inspector" aria-label={`Journée du ${frDayLong(d.work_date)}`}>
      <header className="vws-inspector__head">
        <div>
          <span className={`vws-status vws-status--${meta.tone}`}><span className="vws-status__dot" />{meta.label}</span>
          <h3>{frDayLong(d.work_date)}</h3>
        </div>
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Fermer l’inspecteur" onClick={props.onClose}><X size={16} /></button>
      </header>

      <div className="vws-facts">
        <section>
          <h4>Planning</h4>
          {d.planned ? <p><strong>{range(d.planned.starts_at, d.planned.ends_at)}</strong> · {fmtMin(d.planned.paid_minutes)} rémunérées<br /><small>Pause {breakLabel(d.planned.break_minutes, d.planned.break_paid).toLowerCase()}</small></p> : <p className="vws-muted">Aucun shift prévu.</p>}
        </section>
        <section>
          <h4>Pointage brut</h4>
          {d.clocked ? <p><strong>{range(d.clocked.starts_at, d.clocked.ends_at)}</strong> · {fmtMin(Math.round((new Date(d.clocked.ends_at).getTime() - new Date(d.clocked.starts_at).getTime()) / 60_000))} de présence<br /><small>Jamais modifié — la correction crée une version validée.</small></p> : <p className="vws-muted">{st === 'ABSENCE' ? 'Prévu, non pointé.' : st === 'CLOCKED' ? 'Un pointage existe, sans paire entrée/sortie appariée.' : 'Aucun pointage apparié.'}</p>}
        </section>
        <section>
          <h4>{d.entry?.status === 'PROPOSED' ? 'Temps proposé' : 'Temps validé'}</h4>
          {d.entry ? (
            <p>
              <strong>{range(d.entry.starts_at, d.entry.ends_at)}</strong> · <strong className="vws-paid">{fmtMin(d.entry.paid_minutes)}</strong> rémunérées
              {d.entry.night_minutes ? <> · {fmtMin(d.entry.night_minutes)} de nuit</> : null}
              <br /><small>Pause {breakLabel(d.entry.break_minutes, d.entry.break_paid).toLowerCase()}{d.delta_minutes != null ? ` · écart ${fmtDelta(d.delta_minutes)} vs prévu` : ''}</small>
              {current?.decided_at ? <><br /><small>Décidé le {frDateTime(current.decided_at)}{current.decided_by_name ? ` par ${current.decided_by_name}` : ''}{d.entry.correction_reason_code ? ` · ${reasonLabel(d.entry.correction_reason_code)}${d.entry.correction_note ? ` — ${d.entry.correction_note}` : ''}` : ''}</small></> : null}
            </p>
          ) : <p className="vws-muted">Aucune proposition.</p>}
        </section>

        <section>
          <h4>Anomalies</h4>
          {d.anomalies.length === 0 ? <p className="vws-muted">Aucune.</p> : (
            <ul className="vws-anomlist">
              {d.anomalies.map((a) => (
                <li key={a.id} className={a.resolved ? 'vws-anomlist__item--done' : ''}>
                  <span className="vws-anomlist__head">{a.resolved ? <Check size={13} aria-hidden="true" /> : <AlertTriangle size={13} aria-hidden="true" />} <strong>{ANOMALY_LABELS[a.kind] ?? a.kind}</strong>{a.resolved ? ' · résolue' : ''}</span>
                  <small>{a.detail}</small>
                  {!a.resolved && props.canApprove ? (
                    <span className="vws-resolve">
                      <input className="lc-input" value={resolution[a.id] ?? ''} placeholder="Résolution (obligatoire)" aria-label="Résolution"
                        onChange={(e) => setResolution({ ...resolution, [a.id]: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') void resolve(a.id) }} />
                      <button type="button" className="lc-btn lc-btn--sm" disabled={busy || (resolution[a.id] ?? '').trim().length < 3} onClick={() => void resolve(a.id)}>Résoudre</button>
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h4>Legal</h4>
          {d.entry?.legal_exception ? (
            <p className="vws-legal vws-legal--done"><ShieldAlert size={13} aria-hidden="true" /> Validé en dérogation — motif : {d.entry.legal_exception_reason}{current?.legal_exception_rule ? <><br /><small>{current.legal_exception_rule}</small></> : null}</p>
          ) : d.verdicts.length === 0 ? (
            <p className="vws-muted">{d.entry?.status === 'PROPOSED' ? 'Conforme aux règles applicables.' : 'Évalué au moment de la décision.'}</p>
          ) : (
            <ul className="vws-verdicts">
              {d.verdicts.map((v, i) => (
                <li key={i} className={`vws-verdict vws-verdict--${v.severity.toLowerCase()}`}>
                  <strong>{v.rule}</strong> — {v.message} <small>({v.source}{v.article ? ` · ${v.article}` : ''})</small>
                  {v.severity === 'BLOCK' ? <small className="vws-verdict__hint">Le temps a été effectué : validable en dérogation motivée.</small> : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h4>Historique des versions</h4>
          {d.history.length === 0 ? <p className="vws-muted">Aucune version.</p> : (
            <ol className="vws-versions">
              {d.history.map((v, i) => (
                <li key={v.id} className={v.superseded_by ? 'vws-versions__old' : ''}>
                  <span className="vws-versions__n">v{i + 1}</span>
                  <span>
                    <strong>{range(v.starts_at, v.ends_at)}</strong> · {fmtMin(v.paid_minutes)} · {v.status === 'PROPOSED' ? 'proposée' : v.status === 'CORRECTED' ? 'corrigée' : v.legal_exception ? 'validée en dérogation' : 'validée'}
                    {v.superseded_by ? ' · remplacée' : ''}
                    <br /><small>
                      {v.decided_at ? `${frDateTime(v.decided_at)}${v.decided_by_name ? ` · ${v.decided_by_name}` : ''}` : `proposée le ${frDateTime(v.proposed_at)}`}
                      {v.correction_reason_code ? ` · ${reasonLabel(v.correction_reason_code)}${v.correction_note ? ` — ${v.correction_note}` : ''}` : ''}
                      {v.legal_exception_reason ? ` · dérogation : ${v.legal_exception_reason}` : ''}
                    </small>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section>
          <h4>Aperçu compteur</h4>
          {d.counter_preview.movements.length === 0
            ? <p className="vws-muted">{d.counter_preview.note}</p>
            : <ul>{d.counter_preview.movements.map((m, i) => <li key={i}>{m.type_key} {m.amount > 0 ? '+' : ''}{m.amount} — {m.reason}</li>)}</ul>}
        </section>
      </div>

      {d.entry && !props.correcting ? (
        <footer className="vws-inspector__actions">
          {d.entry.status === 'PROPOSED' ? (
            block
              ? (props.canException ? <button type="button" className="lc-btn lc-btn--danger" onClick={props.onException}><ShieldAlert size={14} aria-hidden="true" /> Valider en dérogation</button> : null)
              : (props.canApprove ? <button type="button" className="lc-btn lc-btn--primary" disabled={open.length > 0} title={open.length ? 'Résoudre les anomalies d’abord' : undefined} onClick={props.onApprove}><Check size={14} aria-hidden="true" /> Valider la journée</button> : null)
          ) : null}
          {props.canCorrect ? <button type="button" className="lc-btn" onClick={props.onStartCorrection}><Pencil size={14} aria-hidden="true" /> Corriger</button> : null}
        </footer>
      ) : null}

      {d.entry && props.correcting ? (
        <CorrectionForm day={d} start={props.correcting.start} end={props.correcting.end} canException={props.canException} onCancel={props.onCancelCorrection} onDone={props.onChanged} />
      ) : null}
    </aside>
  )
}

/* ─── correction motivée, dans l'inspector ─── */
function CorrectionForm(props: { day: Day; start: string; end: string; canException: boolean; onCancel: () => void; onDone: () => Promise<void> }) {
  const e = props.day.entry!
  const toast = useToast()
  const [start, setStart] = useState(props.start)
  const [end, setEnd] = useState(props.end)
  const [breakMin, setBreakMin] = useState(String(e.break_minutes))
  const [breakPaid, setBreakPaid] = useState(e.break_paid)
  const [reason, setReason] = useState<string | null>(e.correction_reason_code ?? 'ERREUR_POINTAGE')
  const [note, setNote] = useState('')
  const [exceptionReason, setExceptionReason] = useState<string | null>(null) // non null = dérogation exigée par le serveur
  const [busy, setBusy] = useState(false)
  const reasonRef = useRef<HTMLDivElement>(null)
  useEffect(() => { reasonRef.current?.querySelector<HTMLElement>('button')?.focus() }, [])

  // L'ancre est la JOURNÉE MÉTIER (work_date), jamais la date civile du début actuel : une
  // nuit peut commencer avant ou après minuit. Le lendemain se dit explicitement, pour le
  // début comme pour la fin (pré-coché d'après la version actuelle) — aucune bascule implicite.
  const baseYmd = props.day.work_date
  const [startNextDay, setStartNextDay] = useState(parisYmd(e.starts_at) > baseYmd)
  const [endNextDay, setEndNextDay] = useState(parisYmd(e.ends_at) > baseYmd)
  const startYmd = startNextDay ? addDays(baseYmd, 1) : baseYmd
  const endYmd = endNextDay ? addDays(baseYmd, 1) : baseYmd
  const startIso = /^\d{2}:\d{2}$/.test(start) ? parisLocalToIso(startYmd, start) : null
  const endIso = /^\d{2}:\d{2}$/.test(end) ? parisLocalToIso(endYmd, end) : null
  const span = startIso && endIso ? Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60_000) : 0
  const ordered = !!startIso && !!endIso && span > 0
  const brk = Math.max(0, Number(breakMin) || 0)
  const paid = Math.max(0, span - (breakPaid ? 0 : brk))
  const valid = ordered && !!reason && (exceptionReason === null || exceptionReason.trim().length >= 3)
  const recap = startIso && endIso ? `${frNum(startYmd)} ${start} → ${frNum(endYmd)} ${end}` : null

  const submit = async () => {
    if (!valid || !startIso || !endIso) return
    setBusy(true)
    const r = await adminMutate<{ error?: string; message?: string; requires_exception?: boolean; warnings?: Array<{ message: string }>; exceptional?: boolean }>(
      'POST', `/api/v1/hr/time/entries/${e.id}/correct`,
      { starts_at: startIso, ends_at: endIso, break_minutes: brk, break_paid: breakPaid, reason_code: reason, ...(note.trim() ? { note: note.trim() } : {}), ...(exceptionReason && exceptionReason.trim().length >= 3 ? { legal_exception_reason: exceptionReason.trim() } : {}) })
    setBusy(false)
    if (r.status >= 400) {
      if (r.data.requires_exception) {
        if (!props.canException) { toast('error', 'Ce temps corrigé est non conforme : une dérogation (permission dédiée) est nécessaire.'); return }
        setExceptionReason(''); toast('error', 'Temps corrigé non conforme : indiquez un motif de dérogation pour le valider quand même.'); return
      }
      toast('error', r.data.message ?? 'Correction refusée.'); return
    }
    toast('ok', `Correction enregistrée${r.data.exceptional ? ' en dérogation' : ''} — l’historique complet est conservé.${r.data.warnings?.length ? ` À noter : ${r.data.warnings[0]!.message}` : ''}`)
    await props.onDone()
  }

  return (
    <form className="vws-correct" onSubmit={(ev) => { ev.preventDefault(); void submit() }}>
      <h4><Pencil size={14} aria-hidden="true" /> Corriger la journée</h4>
      <p className="lc-hint">Le pointage brut n’est jamais modifié : la correction crée une nouvelle version validée, motivée et auditée.</p>
      <p className="vws-correct__base">Journée du <strong>{frDayLong(baseYmd)}</strong> — « lendemain » = {frNum(addDays(baseYmd, 1))}.</p>
      <div className="vws-correct__times">
        <Field label="Début">
          <input type="time" className="lc-input" value={start} onChange={(ev) => setStart(ev.target.value)} aria-label="Début" />
          <Checkbox checked={startNextDay} onChange={setStartNextDay} label="Lendemain" ariaLabel="Le début est le lendemain" />
        </Field>
        <Field label="Fin">
          <input type="time" className="lc-input" value={end} onChange={(ev) => setEnd(ev.target.value)} aria-label="Fin" />
          <Checkbox checked={endNextDay} onChange={setEndNextDay} label="Lendemain" ariaLabel="La fin est le lendemain" />
        </Field>
        <Field label="Pause"><NumberInput value={breakMin} onChange={setBreakMin} unit="min" min={0} max={480} step={5} steppers ariaLabel="Pause (minutes)" /></Field>
      </div>
      <p className={`vws-correct__recap${recap && !ordered ? ' vws-correct__recap--bad' : ''}`} role={recap && !ordered ? 'alert' : undefined}>
        {!recap ? 'Renseignez un début et une fin.'
          : ordered ? <><strong>{recap}</strong> · {fmtMin(span)}</>
            : <><strong>{recap}</strong> — la fin doit suivre le début : vérifiez les cases « Lendemain ».</>}
      </p>
      <label className="vws-correct__paid">
        <Switch checked={breakPaid} onChange={setBreakPaid} disabled={brk === 0} ariaLabel="Pause rémunérée" />
        <span>Pause rémunérée</span>
        <em>Durée validée : <strong>{fmtMin(paid)}</strong>{brk && !breakPaid ? ` (${fmtMin(span)} − ${brk} min)` : ''}</em>
      </label>
      <div ref={reasonRef}>
        <Field label="Motif (obligatoire)"><Combobox value={reason} items={REASONS} onChange={setReason} ariaLabel="Motif de correction" placeholder="Choisir un motif…" /></Field>
      </div>
      <Field label="Note complémentaire"><TextInput value={note} onChange={setNote} placeholder="Facultatif" maxLength={400} /></Field>
      {exceptionReason !== null ? (
        <Field label="Motif de dérogation (non-conformité légale)" helper="Obligatoire — validation exceptionnelle tracée nominativement.">
          <TextInput value={exceptionReason} onChange={setExceptionReason} placeholder="ex. Renfort exceptionnel validé par la Direction" autoFocus maxLength={400} />
        </Field>
      ) : null}
      <div className="vws-correct__actions">
        <button type="button" className="lc-btn lc-btn--ghost" onClick={props.onCancel}>Annuler</button>
        <button type="submit" className={`lc-btn ${exceptionReason !== null ? 'lc-btn--danger' : 'lc-btn--primary'}`} disabled={busy || !valid}>{exceptionReason !== null ? 'Corriger en dérogation' : 'Enregistrer la correction'}</button>
      </div>
    </form>
  )
}

/* ─── validation exceptionnelle : la seule modale, courte et sensible ─── */
function ExceptionModal({ day, onClose, onDone }: { day: Day; onClose: () => void; onDone: () => Promise<void> }) {
  const toast = useToast()
  const block = day.verdicts.find((v) => v.severity === 'BLOCK')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async () => {
    setBusy(true)
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/hr/time/entries/${day.entry!.id}/approve-exception`, { reason: reason.trim() })
    setBusy(false)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', 'Journée validée en dérogation — tracée nominativement dans l’audit.')
    await onDone()
  }
  return (
    <Modal open onClose={onClose} ariaLabel="Valider malgré la non-conformité" width={520}>
      <div className="vws-exception">
        <h3><ShieldAlert size={18} aria-hidden="true" /> Valider malgré la non-conformité</h3>
        <p>{frDayLong(day.work_date)} — {day.entry ? `${range(day.entry.starts_at, day.entry.ends_at)} · ${fmtMin(day.entry.paid_minutes)} rémunérées` : ''}. Le temps a réellement été effectué : vous le validez en dépit d’une règle bloquante. Cette décision est <strong>tracée nominativement</strong>.</p>
        {block ? (
          <dl className="vws-exception__facts">
            <div><dt>Règle</dt><dd>{block.rule}</dd></div>
            <div><dt>Constat</dt><dd>{block.message}</dd></div>
            <div><dt>Source</dt><dd>{block.source}{block.article ? ` · ${block.article}` : ''}</dd></div>
            <div><dt>Impact</dt><dd>Journée validée, marquée « dérogation » ; aucun mouvement de compteur (aucune politique de compensation configurée).</dd></div>
          </dl>
        ) : null}
        <Field label="Motif (obligatoire)"><TextInput value={reason} onChange={setReason} placeholder="ex. Renfort exceptionnel validé par la Direction, incident technique…" autoFocus maxLength={400} /></Field>
        <div className="lc-modal__actions">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={onClose}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--danger" disabled={busy || reason.trim().length < 3} onClick={() => void submit()}>Valider exceptionnellement</button>
        </div>
      </div>
    </Modal>
  )
}

/* ═══════════════════════════ ONGLETS SECONDAIRES ═══════════════════════════ */

function AbsencesTab(props: { days: Day[]; now: number; leaves: Leave[] | null; personId: string; week: string }) {
  const mine = (props.leaves ?? []).filter((l) => l.person_id === props.personId)
  // Même règle que le cœur : une anomalie ouverte ou un pointage présent (has_clock) relève des anomalies, pas des absences.
  const notClocked = props.days.filter(isUnclocked)
  if (mine.length === 0 && notClocked.length === 0) {
    return <p className="vws-empty vws-empty--card">Aucune absence planifiée ni journée prévue sans pointage cette semaine.{props.leaves === null ? ' (Les absences ne sont pas lisibles avec vos droits actuels.)' : ''}</p>
  }
  return (
    <div className="vws-cardlist">
      {mine.map((l, i) => {
        // Une demande en attente n'est JAMAIS présentée comme approuvée : pilule ambre distincte + texte.
        const pending = l.status === 'REQUESTED'
        return (
          <article key={`l${i}`} className="vws-cardrow">
            <span className={`vws-status vws-status--${pending ? 'warn' : 'idle'}`}><span className="vws-status__dot" />{pending ? 'Demande en attente' : 'Absence approuvée'}</span>
            <strong>{l.type_label}</strong>
            <small>Du {frShort(l.starts_on)} au {frShort(l.ends_on)}{pending ? ' — rien n’est validé tant qu’un approbateur n’a pas tranché' : ''}</small>
          </article>
        )
      })}
      {notClocked.map((d) => {
        const past = new Date(d.planned!.ends_at).getTime() < props.now
        return (
          <article key={d.work_date} className="vws-cardrow">
            <span className={`vws-status vws-status--${past ? 'warn' : 'idle'}`}><span className="vws-status__dot" />{past ? 'Prévu, non pointé' : 'À venir'}</span>
            <strong>{frDayLong(d.work_date)}</strong>
            <small>{range(d.planned!.starts_at, d.planned!.ends_at)} · {fmtMin(d.planned!.paid_minutes)} prévues{past ? ' — aucune proposition ne naît sans pointage.' : ''}</small>
          </article>
        )
      })}
      {props.leaves === null ? <p className="lc-hint">Les absences ne sont pas lisibles avec vos droits actuels — seules les journées prévues sans pointage sont listées.</p> : null}
    </div>
  )
}

function HistoryTab(props: { days: Day[] }) {
  const rows = props.days.flatMap((d) => d.history.map((v) => ({ ...v, work_date: d.work_date })))
    .filter((v) => v.status !== 'PROPOSED' || v.superseded_by)
    .sort((a, b) => (b.decided_at ?? b.proposed_at).localeCompare(a.decided_at ?? a.proposed_at))
  if (rows.length === 0) return <p className="vws-empty vws-empty--card">Aucune décision enregistrée cette semaine — l’historique se remplit au fil des validations et corrections.</p>
  return (
    <ol className="vws-cardlist">
      {rows.map((v) => (
        <li key={v.id} className={`vws-cardrow${v.superseded_by ? ' vws-cardrow--old' : ''}`}>
          <span className={`vws-status vws-status--${v.superseded_by ? 'idle' : v.legal_exception ? 'bad' : 'ok'}`}><span className="vws-status__dot" />
            {v.superseded_by ? 'Version remplacée' : v.status === 'CORRECTED' ? 'Correction' : v.legal_exception ? 'Dérogation' : 'Validation'}</span>
          <strong>{frDayLong(v.work_date)} · {range(v.starts_at, v.ends_at)} · {fmtMin(v.paid_minutes)}</strong>
          <small>
            {v.decided_at ? `${frDateTime(v.decided_at)}${v.decided_by_name ? ` · ${v.decided_by_name}` : ''}` : `Proposée le ${frDateTime(v.proposed_at)}`}
            {v.correction_reason_code ? ` · ${reasonLabel(v.correction_reason_code)}${v.correction_note ? ` — ${v.correction_note}` : ''}` : ''}
            {v.legal_exception_reason ? ` · dérogation : ${v.legal_exception_reason}` : ''}
          </small>
        </li>
      ))}
    </ol>
  )
}
